import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const BLOG_URL = process.env.REACT_APP_BLOG_URL || "https://finhabits.blogspot.com/";

/**
 * Landing-page section that pulls the 3 latest Blogger posts via our
 * cached backend proxy and renders them as cards. Hides itself entirely
 * if the feed is unreachable so the page never shows an empty section.
 */
export default function LatestBlogPosts() {
  const [posts, setPosts] = useState(null); // null = loading, [] = none, [...] = posts

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/blog/recent?limit=3`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (!cancelled) setPosts(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setPosts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Loading or empty → render nothing. Better to silently omit than
  // to flash an empty section on the landing page.
  if (!posts || posts.length === 0) return null;

  return (
    <section className="py-16 md:py-20 bg-[#F7F5F0]" data-testid="latest-blog-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#A9712A] mb-2">From the blog</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#2C302E]">
              Habits, tools, and stories
            </h2>
            <p className="text-[#787671] mt-2 text-sm md:text-base max-w-xl">
              New writing on building the financial habit loop. Practical, short,
              and free.
            </p>
          </div>
          <a
            href={`${BLOG_URL}?utm_source=finhabits-app&utm_medium=landing&utm_campaign=blog_header`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-[#2C302E] underline underline-offset-4 hover:text-[#A9712A] transition-colors"
            data-testid="latest-blog-all-link"
          >
            View all posts →
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((p) => (
            <BlogCard key={p.id} post={p} />
          ))}
        </div>
      </div>
    </section>
  );
}

function BlogCard({ post }) {
  const [imageBroken, setImageBroken] = useState(false);
  const published = post.published
    ? new Date(post.published).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";
  const showImage = post.image && !imageBroken;
  return (
    <a
      href={post.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block bg-white border border-[#E6E3DB] rounded-md overflow-hidden hover:border-[#A9712A] transition-colors"
      data-testid="blog-card"
    >
      {showImage ? (
        <div className="aspect-[16/9] overflow-hidden bg-[#F0EDE5]">
          <img
            src={post.image}
            alt=""
            // Eager-load so users on slower connections don't see a
            // missing middle card while the rest of the row has rendered.
            // We only ever show 3 cards so the cost is tiny.
            loading="eager"
            decoding="async"
            onError={() => setImageBroken(true)}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          />
        </div>
      ) : (
        <div className="aspect-[16/9] bg-gradient-to-br from-[#F0EDE5] to-[#E6E3DB]" />
      )}
      <div className="p-5">
        {published ? (
          <p className="text-xs text-[#A9712A] uppercase tracking-wider mb-2">
            {published}
          </p>
        ) : null}
        <h3 className="font-serif text-lg leading-snug text-[#2C302E] group-hover:text-[#A9712A] transition-colors">
          {post.title}
        </h3>
        {post.snippet ? (
          <p className="text-sm text-[#787671] mt-2 line-clamp-3">{post.snippet}</p>
        ) : null}
        <p className="text-xs text-[#2C302E] mt-4 font-medium">Read post →</p>
      </div>
    </a>
  );
}
